import React from 'react';
import Header from '../components/Header';
import FooterComponent from '../components/FooterComponent';

const PrivacyPolicy = () => {
  return (
    <div className='min-h-screen bg-base-200'>
      <Header />
      <div className='max-w-4xl mx-auto py-16 px-4'>
        <h1 className='text-4xl font-bold mb-2'>Privacy Policy</h1>
        <p className='text-base-content/60 mb-8'>Last updated: March 5, 2026</p>

        <div className='space-y-8 text-base-content/80'>
          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>1. INTRODUCTION</h2>
            <p>Welcome to Beanbag ("we", "us", "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have.</p>
            <p className='mt-2'>By using the Beanbag app, you agree to the collection and use of information in accordance with this policy.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>2. INFORMATION WE COLLECT</h2>
            <p>We collect the following categories of information:</p>
            <div className='mt-4 space-y-4'>
              <div>
                <h3 className='font-bold'>Account Data</h3>
                <p>Email address, username, date of birth, password (hashed).</p>
                <p className='text-sm text-base-content/60'>Purpose: Create and secure your account.</p>
              </div>
              <div>
                <h3 className='font-bold'>Profile Data</h3>
                <p>Profile photo, bio, gaming platforms, games played, roles/mains, ranks.</p>
                <p className='text-sm text-base-content/60'>Purpose: Display your profile; power matching.</p>
              </div>
              <div>
                <h3 className='font-bold'>Activity Data</h3>
                <p>Swipes, matches, messages sent and received, login timestamps.</p>
                <p className='text-sm text-base-content/60'>Purpose: Operate core app features; improve algorithms.</p>
              </div>
              <div>
                <h3 className='font-bold'>Device / Technical Data</h3>
                <p>Expo push token, device model, OS version, app version, IP address.</p>
                <p className='text-sm text-base-content/60'>Purpose: Deliver push notifications; security; analytics.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>3. HOW WE USE YOUR INFORMATION</h2>
            <ul className='list-disc list-inside space-y-2'>
              <li>To create and manage your account.</li>
              <li>To match you with other players based on shared games, platforms, and roles.</li>
              <li>To enable real-time chat between matched users.</li>
              <li>To send push notifications (new matches, messages, app updates).</li>
              <li>To improve the App, debug issues, and conduct analytics.</li>
              <li>To enforce our Terms and Conditions and keep the community safe.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>4. INFORMATION SHARING</h2>
            <p>We do not sell your personal information. We may share data only in these circumstances:</p>
            <ul className='list-disc list-inside space-y-2 mt-4'>
              <li><strong>Other users:</strong> Your public profile (username, photo, bio, games, ranks) is visible to other Beanbag users for matching purposes. Your email address is never shared.</li>
              <li><strong>Service providers:</strong> We use Supabase for authentication, database, and storage. These providers process data only as needed to provide their services.</li>
              <li><strong>Legal requirements:</strong> We may disclose information if required by law or in response to a valid legal process.</li>
              <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, user data may be transferred as part of that transaction.</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>5. DATA RETENTION</h2>
            <p>We retain your personal data for as long as your account is active. If you delete your account, we will delete your personal data within 30 days, except where retention is required by law or for legitimate business purposes (e.g., fraud prevention).</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>6. YOUR RIGHTS</h2>
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className='list-disc list-inside space-y-2 mt-4'>
              <li><strong>Access:</strong> Request a copy of the data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Portability:</strong> Request a machine-readable export of your data.</li>
              <li><strong>Objection / Restriction:</strong> Object to or request restriction of certain processing activities.</li>
            </ul>
            <p className='mt-4'>To exercise any of these rights, contact us at <a href='mailto:joaopedro.soares57609@gmail.com' className='link link-primary'>joaopedro.soares57609@gmail.com</a>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>7. SECURITY</h2>
            <p>We implement industry-standard security measures including encrypted connections (HTTPS/TLS), hashed passwords, and access-controlled databases. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>8. CHILDREN'S PRIVACY</h2>
            <p>Beanbag is not directed to anyone under the age of 18. We do not knowingly collect personal information from children under 18. If you believe a minor has provided us with personal information, please contact us immediately and we will remove that information.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>9. THIRD-PARTY LINKS</h2>
            <p>The App may contain links to third-party websites or services (e.g., game platform profiles). We are not responsible for the privacy practices of those third parties. We encourage you to review their privacy policies.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>10. PUSH NOTIFICATIONS</h2>
            <p>We use Expo's push notification service to send you notifications about matches and messages. You can disable notifications at any time in your device's notification settings. We only send notifications that are directly related to your in-app activity.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>11. CHANGES TO THIS POLICY</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via the App or by email. Continued use of the App after updates constitutes acceptance of the revised policy.</p>
          </section>

          <section>
            <h2 className='text-2xl font-bold text-base-content mb-4'>12. CONTACT US</h2>
            <p>If you have questions, concerns, or requests regarding this Privacy Policy, please reach out to us at:</p>
            <div className='mt-4'>
              <p><strong>Beanbag Privacy Team</strong></p>
              <p>Email: <a href='mailto:joaopedro.soares57609@gmail.com' className='link link-primary'>joaopedro.soares57609@gmail.com</a></p>
            </div>
          </section>
        </div>

        <div className='mt-12 pt-8 border-t border-base-content/20 text-center text-base-content/60'>
          <p>© 2026 Beanbag. All rights reserved.</p>
        </div>
      </div>
      <FooterComponent />
    </div>
  );
};

export default PrivacyPolicy;